/**
 * Vehicle sound model registry.
 *
 * This layer is intentionally separate from the current AudioWorklet. It gives
 * the rebuild a measurable target model before we replace the audible synth.
 */

export const SCENARIOS = {
  idle: { rpmRatio: 0, throttle: 0.08, overrun: 0, eventFocus: 'combustion' },
  pull: { rpmRatio: 0.68, throttle: 0.92, overrun: 0, eventFocus: 'exhaust' },
  overrun: { rpmRatio: 0.52, throttle: 0.02, overrun: 0.84, eventFocus: 'burble' },
  shift: { rpmRatio: 0.78, throttle: 0.35, overrun: 0.35, eventFocus: 'shiftUp' },
}

const mustangV8Crossplane = {
  id: 'mustang-v8-crossplane',
  label: 'Ford Mustang GT 5.0 Coyote Crossplane V8',
  engine: {
    cylinders: 8,
    crankType: 'crossplane',
    bankAngleDeg: 90,
    firingOrder: [1, 5, 4, 8, 6, 3, 7, 2],
    idleRpm: 800,
    redlineRpm: 7400,
  },
  orders: {
    idle: [
      { order: 0.5, gain: 1.0 },
      { order: 1, gain: 0.72 },
      { order: 2, gain: 0.58 },
      { order: 4, gain: 0.34 },
    ],
    pull: [
      { order: 2, gain: 1.0 },
      { order: 4, gain: 0.86 },
      { order: 6, gain: 0.58 },
      { order: 0.5, gain: 0.4 },
    ],
    overrun: [
      { order: 0.5, gain: 1.0 },
      { order: 1, gain: 0.82 },
      { order: 2, gain: 0.48 },
      { order: 3, gain: 0.3 },
    ],
    shift: [
      { order: 2, gain: 0.9 },
      { order: 4, gain: 0.82 },
      { order: 8, gain: 0.42 },
    ],
  },
  resonances: {
    exhaust: [
      { id: 'sub-boom', frequencyHz: 74, q: 0.78, gain: 1.0, rpmTrack: 0.08 },
      { id: 'chest', frequencyHz: 146, q: 1.05, gain: 0.84, rpmTrack: 0.12 },
      { id: 'pipe-body', frequencyHz: 322, q: 1.18, gain: 0.66, rpmTrack: 0.18 },
      { id: 'bark', frequencyHz: 920, q: 1.35, gain: 0.46, rpmTrack: 0.24 },
    ],
    intake: [
      { id: 'throat', frequencyHz: 520, q: 0.9, gain: 0.34, rpmTrack: 0.35 },
      { id: 'edge', frequencyHz: 1420, q: 1.25, gain: 0.22, rpmTrack: 0.42 },
    ],
  },
  forcedInduction: { type: 'none', turboCount: 0, spoolRpm: 0, gain: 0, character: 'natural' },
  events: {
    startup: { density: 0.36, brightness: 0.2, decayMs: 420 },
    shiftUp: { density: 0.28, brightness: 0.34, decayMs: 180 },
    shiftDown: { density: 0.34, brightness: 0.42, decayMs: 220 },
    burble: { density: 0.72, brightness: 0.28, decayMs: 340 },
    pop: { density: 0.46, brightness: 0.5, decayMs: 130 },
    gPunch: { density: 0.3, brightness: 0.34, decayMs: 210 },
  },
  dynamics: {
    inertia: 0.58,
    loadResponse: 0.74,
    gearRatios: [3.72, 2.34, 1.7, 1.31, 1.04, 0.85, 0.72, 0.61],
    shiftStrategy: { lowLoad: 0.42, mediumLoad: 0.82, highLoad: 0.93 },
    torqueCurve: [
      { rpm: 900, torque: 0.34 },
      { rpm: 2200, torque: 0.68 },
      { rpm: 4600, torque: 1.0 },
      { rpm: 6800, torque: 0.86 },
      { rpm: 7400, torque: 0.68 },
    ],
  },
}

const RESONANCE_PRESETS = {
  hellcatSupercharged: {
    exhaust: [
      { id: 'hemi-sub', frequencyHz: 68, q: 0.7, gain: 1.08, rpmTrack: 0.06 },
      { id: 'hemi-chest', frequencyHz: 132, q: 0.96, gain: 0.9, rpmTrack: 0.1 },
      { id: 'wide-pipe', frequencyHz: 285, q: 1.1, gain: 0.64, rpmTrack: 0.16 },
      { id: 'blower-bark', frequencyHz: 760, q: 1.2, gain: 0.42, rpmTrack: 0.22 },
    ],
    intake: [
      { id: 'blower-mouth', frequencyHz: 680, q: 0.95, gain: 0.38, rpmTrack: 0.24 },
      { id: 'belt-whistle', frequencyHz: 2260, q: 1.4, gain: 0.3, rpmTrack: 0.54 },
    ],
  },
  lt4Supercharged: {
    exhaust: [
      { id: 'lt4-punch', frequencyHz: 78, q: 0.82, gain: 0.94, rpmTrack: 0.08 },
      { id: 'lt4-body', frequencyHz: 172, q: 1.08, gain: 0.78, rpmTrack: 0.12 },
      { id: 'lt4-pipe', frequencyHz: 386, q: 1.18, gain: 0.58, rpmTrack: 0.18 },
      { id: 'lt4-rasp', frequencyHz: 1040, q: 1.35, gain: 0.44, rpmTrack: 0.26 },
    ],
    intake: [
      { id: 'sharp-rotor', frequencyHz: 760, q: 1.0, gain: 0.36, rpmTrack: 0.28 },
      { id: 'charger-edge', frequencyHz: 2480, q: 1.45, gain: 0.26, rpmTrack: 0.56 },
    ],
  },
  hotVBiturboV8: {
    exhaust: [
      { id: 'hotv-low', frequencyHz: 92, q: 0.85, gain: 0.86, rpmTrack: 0.08 },
      { id: 'hotv-body', frequencyHz: 205, q: 1.05, gain: 0.76, rpmTrack: 0.14 },
      { id: 'hotv-bark', frequencyHz: 540, q: 1.18, gain: 0.5, rpmTrack: 0.22 },
      { id: 'hotv-edge', frequencyHz: 1180, q: 1.28, gain: 0.32, rpmTrack: 0.32 },
    ],
    intake: [
      { id: 'twin-scroll-hush', frequencyHz: 720, q: 0.86, gain: 0.36, rpmTrack: 0.3 },
      { id: 'hotv-spool', frequencyHz: 1720, q: 1.25, gain: 0.3, rpmTrack: 0.5 },
    ],
  },
  v12Biturbo: {
    exhaust: [
      { id: 'v12-silk-low', frequencyHz: 104, q: 0.92, gain: 0.72, rpmTrack: 0.08 },
      { id: 'v12-silk-body', frequencyHz: 246, q: 1.08, gain: 0.68, rpmTrack: 0.15 },
      { id: 'v12-metal', frequencyHz: 690, q: 1.22, gain: 0.44, rpmTrack: 0.24 },
      { id: 'v12-crown-muted', frequencyHz: 1480, q: 1.4, gain: 0.28, rpmTrack: 0.36 },
    ],
    intake: [
      { id: 'large-twin-hush', frequencyHz: 780, q: 0.82, gain: 0.28, rpmTrack: 0.28 },
      { id: 'large-twin-spool', frequencyHz: 1640, q: 1.2, gain: 0.22, rpmTrack: 0.46 },
    ],
  },
  v12NaturallyAspirated: {
    exhaust: [
      { id: 'v12-dry-body', frequencyHz: 156, q: 1.0, gain: 0.62, rpmTrack: 0.12 },
      { id: 'v12-tenor', frequencyHz: 410, q: 1.18, gain: 0.7, rpmTrack: 0.2 },
      { id: 'v12-crown', frequencyHz: 1240, q: 1.35, gain: 0.58, rpmTrack: 0.34 },
      { id: 'v12-glass', frequencyHz: 2680, q: 1.5, gain: 0.36, rpmTrack: 0.48 },
    ],
    intake: [
      { id: 'open-plenum', frequencyHz: 1120, q: 1.0, gain: 0.42, rpmTrack: 0.34 },
      { id: 'ram-crown', frequencyHz: 3180, q: 1.35, gain: 0.28, rpmTrack: 0.5 },
    ],
  },
  aventadorSvjV12NaturallyAspirated: {
    exhaust: [
      { id: 'svj-pressure', frequencyHz: 128, q: 0.9, gain: 0.38, rpmTrack: 0.08 },
      { id: 'svj-metal-body', frequencyHz: 360, q: 1.14, gain: 1, rpmTrack: 0.18 },
      { id: 'svj-rasp', frequencyHz: 980, q: 1.28, gain: 0.88, rpmTrack: 0.31 },
      { id: 'svj-crown', frequencyHz: 2420, q: 1.42, gain: 0.5, rpmTrack: 0.48 },
    ],
    intake: [
      { id: 'svj-open-plenum', frequencyHz: 1040, q: 0.96, gain: 0.54, rpmTrack: 0.32 },
      { id: 'svj-ram-edge', frequencyHz: 2860, q: 1.34, gain: 0.4, rpmTrack: 0.52 },
    ],
  },
  flatSixNaturallyAspirated: {
    exhaust: [
      { id: 'boxer-mid', frequencyHz: 142, q: 0.96, gain: 0.68, rpmTrack: 0.12 },
      { id: 'boxer-metal', frequencyHz: 365, q: 1.18, gain: 0.74, rpmTrack: 0.2 },
      { id: 'boxer-rip', frequencyHz: 980, q: 1.32, gain: 0.5, rpmTrack: 0.34 },
      { id: 'boxer-saw', frequencyHz: 2240, q: 1.42, gain: 0.3, rpmTrack: 0.46 },
    ],
    intake: [
      { id: 'gt3-plenum', frequencyHz: 980, q: 1.0, gain: 0.4, rpmTrack: 0.36 },
      { id: 'gt3-ram', frequencyHz: 2860, q: 1.32, gain: 0.24, rpmTrack: 0.54 },
    ],
  },
  v10NaturallyAspirated: {
    exhaust: [
      { id: 'v10-low', frequencyHz: 126, q: 0.98, gain: 0.66, rpmTrack: 0.1 },
      { id: 'v10-wail', frequencyHz: 455, q: 1.16, gain: 0.78, rpmTrack: 0.22 },
      { id: 'v10-knife', frequencyHz: 1320, q: 1.38, gain: 0.48, rpmTrack: 0.36 },
      { id: 'v10-airglass', frequencyHz: 2920, q: 1.48, gain: 0.28, rpmTrack: 0.52 },
    ],
    intake: [
      { id: 'v10-plenum', frequencyHz: 1180, q: 1.0, gain: 0.36, rpmTrack: 0.34 },
      { id: 'v10-trumpet', frequencyHz: 3340, q: 1.35, gain: 0.22, rpmTrack: 0.5 },
    ],
  },
  lfaV10NaturallyAspirated: {
    exhaust: [
      { id: 'lfa-light-body', frequencyHz: 118, q: 0.94, gain: 0.48, rpmTrack: 0.08 },
      { id: 'lfa-tenor-wail', frequencyHz: 520, q: 1.12, gain: 0.86, rpmTrack: 0.24 },
      { id: 'lfa-carbon-edge', frequencyHz: 1480, q: 1.38, gain: 0.5, rpmTrack: 0.38 },
      { id: 'lfa-yamaha-crown', frequencyHz: 3420, q: 1.52, gain: 0.34, rpmTrack: 0.55 },
    ],
    intake: [
      { id: 'lfa-plenum-song', frequencyHz: 1320, q: 0.98, gain: 0.48, rpmTrack: 0.36 },
      { id: 'lfa-intake-crown', frequencyHz: 3860, q: 1.36, gain: 0.28, rpmTrack: 0.54 },
    ],
  },
  w16QuadTurbo: {
    exhaust: [
      { id: 'w16-pressure', frequencyHz: 88, q: 0.76, gain: 0.9, rpmTrack: 0.06 },
      { id: 'w16-density', frequencyHz: 188, q: 0.98, gain: 0.82, rpmTrack: 0.12 },
      { id: 'w16-mass', frequencyHz: 420, q: 1.08, gain: 0.62, rpmTrack: 0.18 },
      { id: 'w16-muted-edge', frequencyHz: 930, q: 1.18, gain: 0.34, rpmTrack: 0.28 },
    ],
    intake: [
      { id: 'quad-hush', frequencyHz: 620, q: 0.75, gain: 0.42, rpmTrack: 0.2 },
      { id: 'quad-spool-cloud', frequencyHz: 1380, q: 1.05, gain: 0.34, rpmTrack: 0.42 },
    ],
  },
  flatplaneV8Biturbo: {
    exhaust: [
      { id: 'flatplane-throat', frequencyHz: 112, q: 0.95, gain: 0.72, rpmTrack: 0.1 },
      { id: 'flatplane-saw', frequencyHz: 335, q: 1.14, gain: 0.76, rpmTrack: 0.18 },
      { id: 'flatplane-wail', frequencyHz: 1040, q: 1.32, gain: 0.48, rpmTrack: 0.3 },
      { id: 'flatplane-edge', frequencyHz: 2140, q: 1.42, gain: 0.28, rpmTrack: 0.44 },
    ],
    intake: [
      { id: 'amg-hotv-hush', frequencyHz: 820, q: 0.9, gain: 0.3, rpmTrack: 0.3 },
      { id: 'amg-spool-edge', frequencyHz: 1960, q: 1.22, gain: 0.24, rpmTrack: 0.5 },
    ],
  },
}

function derivedModel(id, source, overrides) {
  const model = {
    ...source,
    ...overrides,
    id,
    engine: { ...source.engine, ...overrides.engine },
    orders: { ...source.orders, ...overrides.orders },
    resonances: {
      exhaust: overrides.resonances?.exhaust ?? source.resonances.exhaust,
      intake: overrides.resonances?.intake ?? source.resonances.intake,
    },
    forcedInduction: overrides.forcedInduction ?? source.forcedInduction,
    events: { ...source.events, ...overrides.events },
    dynamics: { ...source.dynamics, ...overrides.dynamics },
  }

  return withCompleteEvents(model)
}

const EVENT_KINDS = [
  'startup',
  'shiftUp',
  'shiftDown',
  'burble',
  'pop',
  'gPunch',
  'whine',
  'bov',
  'wastegate',
]

const EVENT_DEFAULTS = {
  startup: {
    gain: 0.22,
    density: 0.36,
    brightness: 0.2,
    decayMs: 420,
    thumpHz: [82, 48],
    noiseGain: 0.04,
  },
  shiftUp: {
    gain: 0.26,
    density: 0.28,
    brightness: 0.34,
    decayMs: 180,
    thumpHz: [74, 54],
    noiseGain: 0.075,
  },
  shiftDown: {
    gain: 0.3,
    density: 0.34,
    brightness: 0.42,
    decayMs: 220,
    thumpHz: [92, 68],
    noiseGain: 0.1,
    blip: 0.085,
  },
  burble: {
    gain: 0.2,
    density: 0.72,
    brightness: 0.28,
    decayMs: 340,
    thumpHz: [64, 52],
    noiseGain: 0.065,
  },
  pop: {
    gain: 0.18,
    density: 0.46,
    brightness: 0.5,
    decayMs: 130,
    thumpHz: [76, 58],
    noiseGain: 0.065,
  },
  gPunch: {
    gain: 0.22,
    density: 0.3,
    brightness: 0.34,
    decayMs: 210,
    thumpHz: [84, 57],
    noiseGain: 0.055,
  },
  whine: {
    gain: 0,
    density: 0,
    brightness: 0,
    decayMs: 0,
    thumpHz: [640, 420],
    frequencyRatio: 0,
    trigger: 'shiftUnload',
    noiseGain: 0,
  },
  bov: {
    gain: 0,
    density: 0,
    brightness: 0,
    decayMs: 0,
    thumpHz: [860, 320],
    trigger: 'throttleLift',
    noiseGain: 0,
  },
  wastegate: {
    gain: 0,
    density: 0,
    brightness: 0,
    decayMs: 0,
    thumpHz: [520, 210],
    trigger: 'boostLimit',
    noiseGain: 0,
  },
}

function clamp01(value) {
  return Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0))
}

function normalizeEvent(kind, event = {}) {
  const fallback = EVENT_DEFAULTS[kind] ?? EVENT_DEFAULTS.pop
  const density = clamp01(event.density ?? fallback.density)
  const brightness = clamp01(event.brightness ?? fallback.brightness)
  const decayMs = Math.max(0, Math.round(event.decayMs ?? fallback.decayMs))

  return {
    ...fallback,
    ...event,
    gain: clamp01(event.gain ?? fallback.gain),
    density,
    brightness,
    decayMs,
    noiseGain: clamp01(event.noiseGain ?? fallback.noiseGain ?? fallback.gain * 0.32),
    thumpHz: event.thumpHz ?? fallback.thumpHz,
  }
}

function inductionEvents(induction) {
  const normalized = normalizeInduction(induction)
  const gain = clamp01(normalized.gain)
  const turboCount = normalized.turboCount || 0
  const empty = {
    whine: EVENT_DEFAULTS.whine,
    bov: EVENT_DEFAULTS.bov,
    wastegate: EVENT_DEFAULTS.wastegate,
  }

  if (normalized.type === 'supercharger') {
    return {
      ...empty,
      whine: {
        gain: clamp01(0.08 + gain * 0.12),
        density: clamp01(0.35 + gain * 0.48),
        brightness: clamp01(0.56 + gain * 0.24),
        decayMs: 190,
        thumpHz: [900, 520],
        frequencyRatio: normalized.whineRatio,
        trigger: 'shiftUnload',
        noiseGain: clamp01(0.03 + gain * 0.04),
      },
    }
  }

  if (turboCount > 0 || normalized.type.includes('turbo')) {
    return {
      ...empty,
      bov: {
        gain: clamp01(0.07 + gain * 0.08),
        density: clamp01(0.32 + gain * 0.38 + turboCount * 0.04),
        brightness: clamp01(0.58 + turboCount * 0.04),
        decayMs: Math.round(180 + turboCount * 32 + gain * 70),
        thumpHz: [920, 360],
        trigger: 'throttleLift',
        noiseGain: clamp01(0.05 + gain * 0.08),
      },
      wastegate: {
        gain: clamp01(0.06 + gain * 0.06 + turboCount * 0.012),
        density: clamp01(0.24 + gain * 0.34 + turboCount * 0.08),
        brightness: clamp01(0.52 + turboCount * 0.05),
        decayMs: Math.round(130 + turboCount * 20 + gain * 50),
        thumpHz: [520, 210],
        trigger: 'boostLimit',
        noiseGain: clamp01(0.045 + gain * 0.065),
      },
    }
  }

  return empty
}

function completeEvents(events = {}, induction = { type: 'none' }) {
  const boostEvents = inductionEvents(induction)
  return Object.fromEntries(
    EVENT_KINDS.map((kind) => {
      const boostEvent = boostEvents[kind] ?? {}
      return [kind, normalizeEvent(kind, { ...EVENT_DEFAULTS[kind], ...boostEvent, ...events[kind] })]
    }),
  )
}

function withCompleteEvents(model) {
  return {
    ...model,
    events: completeEvents(model.events, model.forcedInduction),
  }
}

export const SOUND_MODELS = {
  [mustangV8Crossplane.id]: withCompleteEvents(mustangV8Crossplane),
  'hellcat-v8-supercharged': derivedModel('hellcat-v8-supercharged', mustangV8Crossplane, {
    label: 'Dodge Hellcat 6.2 HEMI Supercharged V8',
    engine: { idleRpm: 850, redlineRpm: 6200 },
    resonances: RESONANCE_PRESETS.hellcatSupercharged,
    forcedInduction: {
      type: 'supercharger',
      turboCount: 0,
      spoolRpm: 0,
      whineRatio: 30,
      gain: 0.78,
      character: 'belt-whine',
    },
    events: { burble: { density: 0.78, brightness: 0.26, decayMs: 360 } },
    dynamics: { inertia: 0.72, loadResponse: 0.68 },
  }),
  'camaro-zl1-v8-supercharged': derivedModel('camaro-zl1-v8-supercharged', mustangV8Crossplane, {
    label: 'Chevrolet Camaro ZL1 LT4 Supercharged V8',
    engine: { idleRpm: 820, redlineRpm: 6500 },
    resonances: RESONANCE_PRESETS.lt4Supercharged,
    forcedInduction: {
      type: 'supercharger',
      turboCount: 0,
      spoolRpm: 0,
      whineRatio: 28,
      gain: 0.68,
      character: 'sharp-whine',
    },
    dynamics: { inertia: 0.64, loadResponse: 0.76 },
  }),
  'urus-v8-biturbo': derivedModel('urus-v8-biturbo', mustangV8Crossplane, {
    label: 'Lamborghini Urus 4.0 Hot-V Biturbo V8',
    engine: { idleRpm: 800, redlineRpm: 6800 },
    resonances: RESONANCE_PRESETS.hotVBiturboV8,
    forcedInduction: { type: 'biturbo', turboCount: 2, spoolRpm: 2100, gain: 0.7, character: 'hot-v' },
    dynamics: { inertia: 0.78, loadResponse: 0.6 },
  }),
  'gtr-v6-biturbo': derivedModel('gtr-v6-biturbo', mustangV8Crossplane, {
    label: 'Nissan GT-R R35 VR38DETT V6 Biturbo',
    engine: {
      cylinders: 6,
      crankType: 'v6-even-fire',
      bankAngleDeg: 60,
      firingOrder: [1, 2, 3, 4, 5, 6],
      idleRpm: 760,
      redlineRpm: 7100,
    },
    orders: {
      idle: [
        { order: 1.5, gain: 0.72 },
        { order: 3, gain: 1.0 },
        { order: 6, gain: 0.42 },
      ],
      pull: [
        { order: 3, gain: 1.0 },
        { order: 6, gain: 0.82 },
        { order: 9, gain: 0.36 },
      ],
      overrun: [
        { order: 1.5, gain: 0.52 },
        { order: 3, gain: 0.82 },
        { order: 6, gain: 0.34 },
      ],
      shift: [
        { order: 3, gain: 0.88 },
        { order: 6, gain: 0.72 },
        { order: 9, gain: 0.28 },
      ],
    },
    resonances: {
      exhaust: [
        { id: 'v6-body', frequencyHz: 118, q: 0.95, gain: 0.82, rpmTrack: 0.1 },
        { id: 'twin-pipe', frequencyHz: 260, q: 1.1, gain: 0.74, rpmTrack: 0.16 },
        { id: 'metal-bark', frequencyHz: 720, q: 1.2, gain: 0.48, rpmTrack: 0.25 },
      ],
      intake: [
        { id: 'compressor-hush', frequencyHz: 640, q: 0.8, gain: 0.32, rpmTrack: 0.3 },
        { id: 'spool-edge', frequencyHz: 1880, q: 1.3, gain: 0.26, rpmTrack: 0.48 },
      ],
    },
    forcedInduction: { type: 'biturbo', turboCount: 2, spoolRpm: 2400, gain: 0.66, character: 'twin-spool' },
    dynamics: { inertia: 0.62, loadResponse: 0.72 },
  }),
  'supra-i6-turbo': derivedModel('supra-i6-turbo', mustangV8Crossplane, {
    label: 'Toyota GR Supra B58 Inline-Six Single Turbo',
    engine: {
      cylinders: 6,
      crankType: 'inline-six-even-fire',
      bankAngleDeg: 0,
      firingOrder: [1, 5, 3, 6, 2, 4],
      idleRpm: 740,
      redlineRpm: 7000,
    },
    orders: {
      idle: [
        { order: 3, gain: 0.8 },
        { order: 6, gain: 0.44 },
      ],
      pull: [
        { order: 3, gain: 1.0 },
        { order: 6, gain: 0.72 },
        { order: 9, gain: 0.32 },
      ],
      overrun: [
        { order: 3, gain: 0.76 },
        { order: 6, gain: 0.38 },
      ],
      shift: [
        { order: 3, gain: 0.82 },
        { order: 6, gain: 0.66 },
      ],
    },
    resonances: {
      exhaust: [
        { id: 'straight-six-body', frequencyHz: 132, q: 0.92, gain: 0.78, rpmTrack: 0.1 },
        { id: 'single-pipe', frequencyHz: 310, q: 1.05, gain: 0.62, rpmTrack: 0.18 },
        { id: 'turbo-edge', frequencyHz: 980, q: 1.25, gain: 0.36, rpmTrack: 0.3 },
      ],
      intake: [
        { id: 'single-spool', frequencyHz: 860, q: 0.9, gain: 0.38, rpmTrack: 0.34 },
        { id: 'bov-air', frequencyHz: 2200, q: 1.2, gain: 0.24, rpmTrack: 0.5 },
      ],
    },
    forcedInduction: { type: 'turbo', turboCount: 1, spoolRpm: 2200, gain: 0.58, character: 'single-spool' },
    dynamics: { inertia: 0.54, loadResponse: 0.78 },
  }),
  'huayra-v12-biturbo': derivedModel('huayra-v12-biturbo', mustangV8Crossplane, {
    label: 'Pagani Huayra AMG M158 V12 Biturbo',
    engine: {
      cylinders: 12,
      crankType: 'v12-even-fire',
      bankAngleDeg: 60,
      firingOrder: [1, 12, 5, 8, 3, 10, 6, 7, 2, 11, 4, 9],
      idleRpm: 750,
      redlineRpm: 6700,
    },
    resonances: RESONANCE_PRESETS.v12Biturbo,
    forcedInduction: { type: 'biturbo', turboCount: 2, spoolRpm: 1900, gain: 0.58, character: 'large-twin' },
    dynamics: { inertia: 0.7, loadResponse: 0.62 },
  }),
  'f812-v12-na': derivedModel('f812-v12-na', mustangV8Crossplane, {
    label: 'Ferrari 812 Superfast 6.5 NA V12',
    engine: {
      cylinders: 12,
      crankType: 'v12-even-fire',
      bankAngleDeg: 65,
      firingOrder: [1, 7, 5, 11, 3, 9, 6, 12, 2, 8, 4, 10],
      idleRpm: 900,
      redlineRpm: 8900,
    },
    orders: {
      idle: [
        { order: 3, gain: 0.52 },
        { order: 6, gain: 0.82 },
        { order: 12, gain: 0.38 },
      ],
      pull: [
        { order: 6, gain: 1.0 },
        { order: 12, gain: 0.72 },
        { order: 18, gain: 0.3 },
        { order: 3, gain: 0.55 },
      ],
      overrun: [
        { order: 3, gain: 0.5 },
        { order: 6, gain: 0.82 },
        { order: 12, gain: 0.42 },
      ],
      shift: [
        { order: 6, gain: 0.92 },
        { order: 12, gain: 0.72 },
        { order: 18, gain: 0.28 },
      ],
    },
    resonances: RESONANCE_PRESETS.v12NaturallyAspirated,
    dynamics: { inertia: 0.42, loadResponse: 0.9 },
  }),
  'aventador-svj-v12-na': derivedModel('aventador-svj-v12-na', mustangV8Crossplane, {
    label: 'Lamborghini Aventador SVJ L539 6.5 NA V12',
    engine: {
      cylinders: 12,
      crankType: 'v12-even-fire',
      bankAngleDeg: 60,
      firingOrder: [1, 12, 4, 9, 2, 11, 6, 7, 3, 10, 5, 8],
      idleRpm: 880,
      redlineRpm: 8700,
    },
    orders: {
      idle: [
        { order: 3, gain: 0.44 },
        { order: 6, gain: 0.86 },
        { order: 12, gain: 0.46 },
      ],
      pull: [
        { order: 6, gain: 1.0 },
        { order: 12, gain: 0.82 },
        { order: 18, gain: 0.42 },
        { order: 3, gain: 0.38 },
      ],
      overrun: [
        { order: 3, gain: 0.42 },
        { order: 6, gain: 0.88 },
        { order: 12, gain: 0.52 },
      ],
      shift: [
        { order: 6, gain: 0.94 },
        { order: 12, gain: 0.78 },
        { order: 18, gain: 0.38 },
      ],
    },
    resonances: RESONANCE_PRESETS.aventadorSvjV12NaturallyAspirated,
    events: {
      burble: { density: 0.44, brightness: 0.42, decayMs: 260 },
      pop: { density: 0.42, brightness: 0.58, decayMs: 110 },
      shiftUp: { density: 0.36, brightness: 0.56, decayMs: 145 },
      shiftDown: { density: 0.46, brightness: 0.62, decayMs: 180 },
    },
    dynamics: { inertia: 0.46, loadResponse: 0.88 },
  }),
  'gt3-flat-six-na': derivedModel('gt3-flat-six-na', mustangV8Crossplane, {
    label: 'Porsche 911 GT3 4.0 Flat-Six',
    engine: {
      cylinders: 6,
      crankType: 'boxer-six',
      bankAngleDeg: 180,
      firingOrder: [1, 6, 2, 4, 3, 5],
      idleRpm: 900,
      redlineRpm: 9000,
    },
    orders: {
      pull: [
        { order: 3, gain: 1.0 },
        { order: 6, gain: 0.76 },
        { order: 9, gain: 0.48 },
      ],
    },
    resonances: RESONANCE_PRESETS.flatSixNaturallyAspirated,
    dynamics: { inertia: 0.36, loadResponse: 0.94 },
  }),
  'r8-v10-na': derivedModel('r8-v10-na', mustangV8Crossplane, {
    label: 'Audi R8 5.2 NA V10',
    engine: {
      cylinders: 10,
      crankType: 'v10-even-fire',
      bankAngleDeg: 90,
      firingOrder: [1, 6, 5, 10, 2, 7, 3, 8, 4, 9],
      idleRpm: 850,
      redlineRpm: 8700,
    },
    orders: {
      idle: [
        { order: 2.5, gain: 0.65 },
        { order: 5, gain: 1.0 },
        { order: 10, gain: 0.32 },
      ],
      pull: [
        { order: 5, gain: 1.0 },
        { order: 10, gain: 0.82 },
        { order: 2.5, gain: 0.45 },
        { order: 15, gain: 0.36 },
      ],
      overrun: [
        { order: 2.5, gain: 0.58 },
        { order: 5, gain: 0.82 },
        { order: 10, gain: 0.34 },
      ],
      shift: [
        { order: 5, gain: 0.92 },
        { order: 10, gain: 0.72 },
        { order: 15, gain: 0.3 },
      ],
    },
    resonances: RESONANCE_PRESETS.v10NaturallyAspirated,
    dynamics: { inertia: 0.44, loadResponse: 0.88 },
  }),
  'lfa-v10-na': derivedModel('lfa-v10-na', mustangV8Crossplane, {
    label: 'Lexus LFA 1LR-GUE 4.8 NA V10',
    engine: {
      cylinders: 10,
      crankType: 'v10-even-fire',
      bankAngleDeg: 72,
      firingOrder: [1, 6, 5, 10, 2, 7, 3, 8, 4, 9],
      idleRpm: 900,
      redlineRpm: 9000,
    },
    orders: {
      idle: [
        { order: 2.5, gain: 0.46 },
        { order: 5, gain: 1.0 },
        { order: 10, gain: 0.36 },
      ],
      pull: [
        { order: 5, gain: 1.0 },
        { order: 10, gain: 0.9 },
        { order: 15, gain: 0.48 },
        { order: 2.5, gain: 0.32 },
      ],
      overrun: [
        { order: 2.5, gain: 0.42 },
        { order: 5, gain: 0.84 },
        { order: 10, gain: 0.4 },
      ],
      shift: [
        { order: 5, gain: 0.9 },
        { order: 10, gain: 0.78 },
        { order: 15, gain: 0.42 },
      ],
    },
    resonances: RESONANCE_PRESETS.lfaV10NaturallyAspirated,
    events: {
      burble: { density: 0.28, brightness: 0.48, decayMs: 210 },
      pop: { density: 0.22, brightness: 0.54, decayMs: 95 },
      shiftUp: { density: 0.26, brightness: 0.58, decayMs: 120 },
      shiftDown: { density: 0.34, brightness: 0.64, decayMs: 155 },
    },
    dynamics: { inertia: 0.3, loadResponse: 0.98 },
  }),
  'chiron-w16-quad-turbo': derivedModel('chiron-w16-quad-turbo', mustangV8Crossplane, {
    label: 'Bugatti Chiron 8.0 W16 Quad-Turbo',
    engine: {
      cylinders: 16,
      crankType: 'w16',
      bankAngleDeg: 90,
      firingOrder: [1, 14, 9, 4, 7, 12, 15, 6, 13, 2, 11, 8, 3, 16, 5, 10],
      idleRpm: 800,
      redlineRpm: 6700,
    },
    forcedInduction: {
      type: 'quad-turbo',
      turboCount: 4,
      spoolRpm: 1700,
      gain: 0.82,
      character: 'dense-quad',
    },
    orders: {
      idle: [
        { order: 4, gain: 0.72 },
        { order: 8, gain: 1.0 },
        { order: 16, gain: 0.38 },
      ],
      pull: [
        { order: 8, gain: 1.0 },
        { order: 4, gain: 0.72 },
        { order: 16, gain: 0.55 },
        { order: 24, gain: 0.2 },
      ],
      overrun: [
        { order: 4, gain: 0.6 },
        { order: 8, gain: 0.86 },
        { order: 16, gain: 0.34 },
      ],
      shift: [
        { order: 8, gain: 0.9 },
        { order: 16, gain: 0.58 },
        { order: 24, gain: 0.22 },
      ],
    },
    resonances: RESONANCE_PRESETS.w16QuadTurbo,
    dynamics: { inertia: 0.88, loadResponse: 0.56 },
  }),
  'amg-gt-flatplane-v8-biturbo': derivedModel('amg-gt-flatplane-v8-biturbo', mustangV8Crossplane, {
    label: 'Mercedes-AMG GT Black Series Flatplane Biturbo V8',
    engine: {
      crankType: 'flatplane',
      firingOrder: [1, 5, 3, 7, 4, 8, 2, 6],
      idleRpm: 850,
      redlineRpm: 7200,
    },
    orders: {
      idle: [
        { order: 2, gain: 0.72 },
        { order: 4, gain: 1.0 },
        { order: 8, gain: 0.42 },
      ],
      pull: [
        { order: 4, gain: 1.0 },
        { order: 8, gain: 0.72 },
        { order: 12, gain: 0.38 },
      ],
    },
    resonances: RESONANCE_PRESETS.flatplaneV8Biturbo,
    forcedInduction: {
      type: 'biturbo',
      turboCount: 2,
      spoolRpm: 2300,
      gain: 0.46,
      character: 'flatplane-hot-v',
    },
    dynamics: { inertia: 0.48, loadResponse: 0.82 },
  }),
}

export function getSoundModel(id) {
  const model = SOUND_MODELS[id]
  if (!model) throw new Error(`Unknown sound model: ${id}`)
  return model
}

export function firingFrequencyHz(model, rpm) {
  return (rpm / 60 / 2) * model.engine.cylinders
}

export function orderFrequencyHz(order, rpm) {
  return (rpm / 60) * order
}

function rpmForScenario(model, scenarioName) {
  const scenario = SCENARIOS[scenarioName]
  if (!scenario) throw new Error(`Unknown sound scenario: ${scenarioName}`)
  return Math.round(
    model.engine.idleRpm + (model.engine.redlineRpm - model.engine.idleRpm) * scenario.rpmRatio,
  )
}

export function describeScenario(model, scenarioName) {
  const scenario = SCENARIOS[scenarioName]
  if (!scenario) throw new Error(`Unknown sound scenario: ${scenarioName}`)
  const rpm = rpmForScenario(model, scenarioName)
  const orderSet = model.orders[scenarioName] ?? model.orders.pull

  return {
    scenario: scenarioName,
    rpm,
    throttle: scenario.throttle,
    overrun: scenario.overrun,
    eventFocus: scenario.eventFocus,
    firingHz: firingFrequencyHz(model, rpm),
    dominantOrders: orderSet.map((entry) => ({
      ...entry,
      frequencyHz: orderFrequencyHz(entry.order, rpm),
    })),
  }
}

export function describeFiringCadence(model) {
  const cadence = buildFireCadence(model)
  const cycleDeg = 720
  const sortedOffsets = [...cadence.offsets].sort((a, b) => a - b)
  const intervalsDeg = sortedOffsets.map((offset, index) => {
    const next = sortedOffsets[(index + 1) % sortedOffsets.length]
    const interval = ((next - offset + 1) % 1 || 1) * cycleDeg
    return Math.round(interval)
  })

  return {
    cycleDeg,
    crankType: model.engine.crankType,
    fireCount: model.engine.cylinders,
    offsets: sortedOffsets,
    intervalsDeg,
    weights: cadence.weights,
    cycleSkew: cadence.cycleSkew,
    signature: cadence.signature,
  }
}

function packOrders(entries = []) {
  return entries.map((entry) => [entry.order, entry.gain])
}

function packResonances(entries = []) {
  return entries.map((entry) => [entry.frequencyHz, entry.q, entry.gain, entry.rpmTrack])
}

function packEvents(events = {}) {
  return Object.fromEntries(
    EVENT_KINDS.map((kind) => {
      const event = normalizeEvent(kind, events[kind])
      return [kind, { ...event, thumpHz: [...event.thumpHz] }]
    }),
  )
}

function buildFireCadence(model) {
  const cylinders = model.engine.cylinders
  const crankType = model.engine.crankType
  const firingOrder = model.engine.firingOrder ?? Array.from({ length: cylinders }, (_, index) => index + 1)
  const baseOffsets = Array.from({ length: cylinders }, (_, index) => index / cylinders)
  const uneven = crankType === 'crossplane' || crankType.includes('v6')
  const cycleSkew = uneven
    ? crankType === 'crossplane'
      ? 0.12
      : 0.025
    : crankType === 'flatplane'
      ? 0.02
      : 0
  const offsets = baseOffsets
  const weights = Array.from({ length: cylinders }, (_, index) => {
    const cylinder = firingOrder[index] ?? index + 1
    const bankPulse = cylinder % 2 === 0 ? -1 : 1
    const orderPulse = index % 2 === 0 ? 1 : -1
    if (crankType === 'crossplane') return 1 + bankPulse * 0.1 + orderPulse * 0.04
    if (crankType.includes('flatplane')) return 1 + orderPulse * 0.025 + bankPulse * 0.01
    if (crankType.includes('v6')) return 1 + bankPulse * 0.06 + orderPulse * 0.025
    if (crankType.includes('w16')) return 1 + bankPulse * 0.035 + orderPulse * 0.015
    if (crankType.includes('v10')) return 1 + bankPulse * 0.035 + orderPulse * 0.02
    if (crankType.includes('v12')) return 1 + bankPulse * 0.02 + orderPulse * 0.01
    return 1 + bankPulse * 0.01
  })
  const signature = `${crankType}:${firingOrder.join('-')}:${weights.map((weight) => weight.toFixed(2)).join(',')}`

  return { offsets, weights, cycleSkew, crankType, signature }
}

function normalizeInduction(induction = { type: 'none' }) {
  const turboCount =
    induction.turboCount ??
    induction.count ??
    (induction.type === 'quad-turbo'
      ? 4
      : induction.type === 'biturbo'
        ? 2
        : induction.type === 'turbo'
          ? 1
          : 0)

  return {
    type: induction.type ?? 'none',
    turboCount,
    layout: induction.layout ?? induction.type ?? 'none',
    spoolRpm: induction.spoolRpm ?? 0,
    whineRatio: induction.whineRatio ?? 0,
    gain: induction.gain ?? 0,
    character: induction.character ?? 'neutral',
  }
}

function averageGain(entries = []) {
  if (!entries.length) return 0
  return clamp01(entries.reduce((sum, entry) => sum + (entry.gain || 0), 0) / entries.length)
}

function lowResonanceGain(entries = []) {
  const lowEntries = entries.filter((entry) => entry.frequencyHz <= 360)
  return averageGain(lowEntries.length ? lowEntries : entries)
}

export function deriveLayerDNA(model) {
  const induction = normalizeInduction(model.forcedInduction)
  const events = completeEvents(model.events, model.forcedInduction)
  const cylinders = model.engine.cylinders
  const crank = model.engine.crankType || ''
  const highRev = clamp01((model.engine.redlineRpm - 6200) / 3000)
  const smoothCylinders = clamp01((cylinders - 4) / 12)
  const crossplane = crank === 'crossplane' ? 1 : 0
  const flatOrBoxer = crank.includes('flat') || crank.includes('boxer') ? 1 : 0
  const v6Roughness = crank.includes('v6') ? 0.25 : 0
  const w16 = crank.includes('w16') ? 1 : 0
  const exhaustGain = averageGain(model.resonances.exhaust)
  const lowExhaust = lowResonanceGain(model.resonances.exhaust)
  const intakeGain = averageGain(model.resonances.intake)
  const turboCount = induction.turboCount || 0
  const turboFamily = turboCount > 0 || induction.type.includes('turbo')
  const supercharger = induction.type === 'supercharger'
  const boostGain = clamp01(induction.gain || 0)

  return {
    combustion: clamp01(0.36 + events.burble.density * 0.18 + crossplane * 0.16 + v6Roughness * 0.12),
    exhaustBody: clamp01(
      0.3 + exhaustGain * 0.12 + lowExhaust * 0.34 + crossplane * 0.2 + (1 - smoothCylinders) * 0.08,
    ),
    intake: clamp01(0.2 + intakeGain * 0.36 + highRev * 0.32 + flatOrBoxer * 0.14 + (turboFamily ? 0.06 : 0)),
    mechanical: clamp01(
      0.12 + highRev * 0.36 + flatOrBoxer * 0.16 + (supercharger ? 0.08 : 0) - turboCount * 0.03,
    ),
    inductionWhine: clamp01(
      supercharger ? 0.22 + boostGain * 0.66 : turboFamily ? 0.03 + boostGain * 0.16 : 0,
    ),
    turboAir: clamp01(turboFamily ? 0.14 + boostGain * 0.38 + turboCount * 0.06 : 0),
    pop: clamp01(
      0.1 +
        events.pop.density * 0.28 +
        events.burble.density * 0.32 +
        crossplane * 0.18 +
        (supercharger ? boostGain * 0.08 : 0) -
        smoothCylinders * 0.14 -
        turboCount * 0.025,
    ),
    smoothness: clamp01(
      0.25 + smoothCylinders * 0.42 + turboCount * 0.08 + w16 * 0.18 - crossplane * 0.12 - v6Roughness * 0.08,
    ),
    orderBlend: clamp01(0.42 + smoothCylinders * 0.24 + highRev * 0.12),
    overrunRoughness: clamp01(
      0.18 + events.burble.density * 0.5 + events.pop.density * 0.22 + crossplane * 0.1,
    ),
  }
}

export function resolveSoundEvent(modelOrId, kind, runtime = {}) {
  const model = typeof modelOrId === 'string' ? getSoundModel(modelOrId) : modelOrId
  const events = completeEvents(model?.events, model?.forcedInduction)
  const event = events[kind] ?? events.pop
  const gainScale = runtime.gainScale ?? 1
  const densityScale = runtime.densityScale ?? 1

  return {
    ...event,
    gain: clamp01(event.gain * gainScale),
    density: clamp01(event.density * densityScale),
    thumpHz: [...event.thumpHz],
  }
}

export function toWorkletSoundPacket(model) {
  const events = completeEvents(model.events, model.forcedInduction)

  return {
    type: 'soundModel',
    id: model.id,
    engine: {
      cylinders: model.engine.cylinders,
      crankType: model.engine.crankType,
      idleRpm: model.engine.idleRpm,
      redlineRpm: model.engine.redlineRpm,
    },
    orders: {
      idle: packOrders(model.orders.idle),
      pull: packOrders(model.orders.pull),
      overrun: packOrders(model.orders.overrun),
      shift: packOrders(model.orders.shift),
    },
    resonances: {
      exhaust: packResonances(model.resonances.exhaust),
      intake: packResonances(model.resonances.intake),
    },
    fireCadence: buildFireCadence(model),
    induction: normalizeInduction(model.forcedInduction),
    events: packEvents(events),
    layerDNA: deriveLayerDNA(model),
  }
}
